import { Send as SendIcon, Visibility as ViewIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { User } from "firebase/auth";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { tutorials } from "../config/tutorials";
import { useRealtimeAgents } from "../hooks/useRealtimeAgents";
import { useRealtimeChat } from "../hooks/useRealtimeChat";
import { usePageTutorial } from "../hooks/useTutorial";
import { AgentMessagingService } from "../services/AgentMessagingService";
import { auth } from "../services/firebase";
import { userProfileService } from "../services/UserProfileService";
import { AgentChatInfo } from "../types";
import { DateUtils } from "../utils/dateUtils";
import { AgentAvatar } from "./AgentAvatar";
import { MessageList } from "./AgentChat/MessageList";
import { CoachPlanModal } from "./CoachPlanModal";
import { HelpIcon } from "./HelpIcon";

// Utility function to format relative time
const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return "Just now";
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  } else {
    return DateUtils.formatForDisplay(date);
  }
};

// Conversation starters component
const ConversationStarters: React.FC<{
  starters: string[];
  onSelect: (starter: string) => void;
  disabled?: boolean;
}> = ({ starters, onSelect, disabled = false }) => {
  if (!starters || starters.length === 0) {
    return null;
  }

  return (
    <Box sx={{ px: 2, pt: 2, pb: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block", textAlign: "center" }}>
        Try asking:
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
        {starters.map((starter, index) => (
          <Chip
            key={index}
            label={starter}
            onClick={() => !disabled && onSelect(starter)}
            disabled={disabled}
            sx={{
              cursor: disabled ? "default" : "pointer",
              "&:hover": {
                bgcolor: disabled ? undefined : "action.hover",
              },
            }}
            size="small"
          />
        ))}
      </Box>
    </Box>
  );
};

// Separate component for input handling to prevent re-renders
const ChatInput: React.FC<{
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  sending?: boolean;
  conversationStarters?: string[];
}> = ({ onSendMessage, disabled = false, sending = false, conversationStarters }) => {
  const [inputValue, setInputValue] = useState("");

  const handleSendMessage = async (message?: string) => {
    const messageContent = message || inputValue.trim();
    if (!messageContent) {
      return;
    }

    setInputValue("");
    onSendMessage(messageContent);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleStarterClick = (starter: string) => {
    handleSendMessage(starter);
  };

  return (
    <Box sx={{ borderTop: 1, borderColor: "divider" }}>
      {conversationStarters && conversationStarters.length > 0 && <ConversationStarters starters={conversationStarters} onSelect={handleStarterClick} disabled={disabled || sending} />}
      <Box sx={{ p: 2, pt: conversationStarters && conversationStarters.length > 0 ? 1 : 2 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            disabled={disabled}
            variant="outlined"
            size="small"
          />
          <IconButton onClick={() => handleSendMessage()} disabled={!inputValue.trim() || disabled || sending} color="primary" size="small">
            {sending ? <CircularProgress size={20} /> : <SendIcon />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

interface AgentChatInterfaceProps {
  className?: string;
}

export const AgentChatInterface: React.FC<AgentChatInterfaceProps> = ({ className }) => {
  const { agentId: urlAgentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentAgent, setCurrentAgent] = useState<AgentChatInfo | null>(null);
  const [loadingAgent, setLoadingAgent] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [executingActions, setExecutingActions] = useState<Set<string>>(new Set());
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [userTimezone, setUserTimezone] = useState<string>("");
  const [conversationStarters, setConversationStarters] = useState<string[] | undefined>(undefined);

  usePageTutorial("chat_page_tour", tutorials.chat);

  // Load user timezone
  useEffect(() => {
    const loadUserTimezone = async () => {
      try {
        const profile = await userProfileService.getUserProfile();
        if (profile?.timezone) {
          setUserTimezone(profile.timezone);
        } else {
          // Fallback to browser timezone
          setUserTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
        }
      } catch (error) {
        console.error("Error loading user timezone:", error);
        // Fallback to browser timezone
        setUserTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
      }
    };

    loadUserTimezone();
  }, []);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastAgentIdRef = useRef<string | null>(null);

  // Real-time hooks
  const { agents, refresh: refreshAgents } = useRealtimeAgents();
  const { messages, typingUsers, sendMessage, refresh: refreshChat, sending } = useRealtimeChat(urlAgentId || "");

  // Mark messages as read when viewing the chat
  useEffect(() => {
    if (urlAgentId && currentAgent?.threadId) {
      // Mark messages as read when user is viewing the chat
      AgentMessagingService.markMessagesAsRead(currentAgent.threadId).catch((error) => {
        console.error("Error marking messages as read:", error);
      });
    }
  }, [urlAgentId, currentAgent?.threadId]);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Scroll to bottom when typing indicator appears/disappears
  useEffect(() => {
    const aiTypingUsers = typingUsers.filter((user) => user.type === "ai");
    if (aiTypingUsers.length > 0) {
      // Small delay to ensure the typing indicator is rendered
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [typingUsers]);

  // Handle authentication state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Update conversation starters only when agent ID changes
  useEffect(() => {
    if (urlAgentId && agents.length > 0 && urlAgentId !== lastAgentIdRef.current) {
      const targetAgent = agents.find((agent) => agent.agentId === urlAgentId);
      if (targetAgent && targetAgent.conversationStarters) {
        // Update conversation starters and track the agent ID
        setConversationStarters(targetAgent.conversationStarters);
        lastAgentIdRef.current = urlAgentId;
      }
    } else if (!urlAgentId) {
      // Clear conversation starters if no agent selected
      lastAgentIdRef.current = null;
      setConversationStarters(undefined);
    }
  }, [urlAgentId, agents]);

  // Set current agent when URL changes
  useEffect(() => {
    setLoadingAgent(true);
    if (urlAgentId && agents.length > 0) {
      const targetAgent = agents.find((agent) => agent.agentId === urlAgentId);
      if (targetAgent) {
        setCurrentAgent(targetAgent);
        setError(null);
        setLoadingAgent(false);
      } else {
        setError(`Agent ${urlAgentId} not found`);
        setLoadingAgent(false);
      }
    } else if (agents.length > 0 && !currentAgent) {
      // Default to first agent (most recent) if no specific agent in URL
      // Navigate to the most recent agent to update the URL
      navigate(`/chat/${agents[0].agentId}`, { replace: true });
      setLoadingAgent(false);
    } else if (agents.length === 0 && urlAgentId) {
      // Still loading agents, keep loading state
      setLoadingAgent(true);
    } else {
      setLoadingAgent(false);
    }
  }, [urlAgentId, agents, currentAgent, navigate]);

  // Handle sending a message
  const handleSendMessage = async (messageContent: string) => {
    if (!urlAgentId) {
      return;
    }

    try {
      setError(null);
      await sendMessage(messageContent);
    } catch (error) {
      setError("Failed to send message");
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    try {
      setError(null);
      // Refresh both agents and chat data
      refreshAgents();
      refreshChat();
      console.log("Refreshing chat data...");
    } catch (error) {
      setError("Failed to refresh");
    }
  };

  // Render typing indicators (only for AI)
  const renderTypingIndicator = () => {
    const aiTypingUsers = typingUsers.filter((user) => user.type === "ai");
    // console.log("Typing indicator debug:", { typingUsers, aiTypingUsers, currentAgent });
    if (aiTypingUsers.length === 0) return null;

    // Use the agent name from the typing data, fallback to current agent name
    const agentName = aiTypingUsers[0]?.name || currentAgent?.agentName || "Agent";

    return (
      <Box sx={{ p: 2, fontStyle: "italic", color: "text.secondary" }}>
        <Typography variant="body2">{agentName} is thinking...</Typography>
      </Box>
    );
  };

  // Action execution handler (always uses override for proposals)
  const handleExecuteAction = async (actionId: string) => {
    try {
      // Add to executing actions set
      setExecutingActions((prev) => new Set(prev).add(actionId));

      console.log("AgentChatInterface: Executing action by ID:", actionId);
      await AgentMessagingService.executeAction(actionId, true); // Always use override for proposals
      console.log("AgentChatInterface: Action executed successfully:", actionId);

      // Show success message
      setError(null);
      setSuccessMessage(`Action ${actionId} executed successfully!`);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("AgentChatInterface: Failed to execute action:", error);
      setError("Failed to execute action");
      setSuccessMessage(null);
    } finally {
      // Remove from executing actions set
      setExecutingActions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(actionId);
        return newSet;
      });
    }
  };

  // Action ignore handler
  const handleIgnoreAction = async (actionId: string) => {
    try {
      console.log("AgentChatInterface: Ignoring action by ID:", actionId);
      await AgentMessagingService.ignoreAction(actionId);

      setError(null);
      setSuccessMessage(`Action ${actionId} ignored`);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("AgentChatInterface: Failed to ignore action:", error);
      setError("Failed to ignore action");
      setSuccessMessage(null);
    }
  };

  const handleViewPlan = () => {
    if (!currentAgent?.currentPlan) {
      setError("No plan found for this coach");
      return;
    }

    setPlanModalOpen(true);
  };

  const isCoach = () => {
    if (!currentAgent) return false;
    // Check userFriendlyType (e.g., "Sales Coach", "Product Coach")
    if (currentAgent.userFriendlyType?.includes("Coach")) {
      return true;
    }
    // Check agentType (e.g., "SalesCoachAgent", "ProductCoachAgent")
    if (currentAgent.agentType?.endsWith("CoachAgent")) {
      return true;
    }
    return false;
  };

  if (!currentUser) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Sign in to chat with your agents
            </Typography>
            <Button variant="contained" onClick={() => (window.location.href = "/login")}>
              Sign In
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" className={className}>
      <Box display="flex" flexDirection="row" flex={1} overflow="hidden">
        {/* Agent List */}
        <Paper sx={{ width: 320, flexShrink: 0, p: 2, borderRight: 1, borderColor: "divider", overflowY: "auto" }} data-tutorial="agent-list">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Agents</Typography>
            <HelpIcon
              tutorialConfig={tutorials.chat}
              pageDescription="Chat with your AI agents to get help with tasks, ask questions, and receive personalized assistance. Each agent has different specialties!"
            />
          </Box>
          <List>
            {agents.map((agent) => (
              <ListItem key={agent.agentId} disablePadding>
                <ListItemButton
                  selected={currentAgent?.agentId === agent.agentId}
                  onClick={() => navigate(`/chat/${agent.agentId}`)}
                  sx={{
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: currentAgent?.agentId === agent.agentId ? "action.selected" : undefined,
                  }}
                >
                  <ListItemAvatar>
                    <Box position="relative" display="inline-block">
                      <AgentAvatar avatarPath={agent.avatarPath} name={agent.agentName} size={40} />
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 2,
                          right: 2,
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          bgcolor: "#4caf50",
                          border: "2px solid white",
                        }}
                      />
                    </Box>
                  </ListItemAvatar>
                  <ListItemText primary={`${agent.agentName}, ${agent.userFriendlyType}`} secondary={agent.lastMessageTime ? formatRelativeTime(new Date(agent.lastMessageTime)) : "No messages yet"} />
                  {agent.unreadCount > 0 && <Badge color="primary" badgeContent={agent.unreadCount} />}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
        {/* Chat Area */}
        <Box flex={1} display="flex" flexDirection="column" overflow="hidden">
          {currentAgent ? (
            <>
              {/* Chat Header */}
              <Paper sx={{ p: 2, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">{currentAgent.agentName}</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <IconButton onClick={handleRefresh} size="small" title="Refresh">
                      <RefreshIcon />
                    </IconButton>
                    {isCoach() && currentAgent?.currentPlan && (
                      <Button variant="outlined" size="small" startIcon={<ViewIcon />} onClick={handleViewPlan}>
                        View Plan
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>
              {/* Messages */}
              <Box ref={messagesContainerRef} flex={1} overflow="auto" p={2} sx={{ minHeight: 0 }} data-tutorial="chat-history">
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
                {successMessage && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {successMessage}
                  </Alert>
                )}
                {messages.length === 0 && conversationStarters && conversationStarters.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "300px",
                      textAlign: "center",
                      gap: 3,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Start a conversation with {currentAgent.agentName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Try one of these conversation starters:
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "center", maxWidth: "800px" }}>
                        {conversationStarters.map((starter, index) => (
                          <Chip
                            key={index}
                            label={starter}
                            onClick={() => handleSendMessage(starter)}
                            disabled={sending}
                            sx={{
                              cursor: sending ? "default" : "pointer",
                              py: 2.5,
                              px: 1,
                              fontSize: "0.875rem",
                              "&:hover": {
                                bgcolor: sending ? undefined : "primary.main",
                                color: sending ? undefined : "primary.contrastText",
                              },
                            }}
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <MessageList
                      messages={messages}
                      onExecuteAction={handleExecuteAction}
                      onIgnoreAction={handleIgnoreAction}
                      executingActions={executingActions}
                      agentNames={agents.reduce(
                        (acc, agent) => {
                          acc[agent.agentId] = agent.agentName;
                          return acc;
                        },
                        {} as { [agentId: string]: string }
                      )}
                      agentAvatars={agents.reduce(
                        (acc, agent) => {
                          acc[agent.agentId] = agent.avatarPath;
                          return acc;
                        },
                        {} as { [agentId: string]: string | undefined }
                      )}
                      userTimezone={userTimezone}
                    />
                    {renderTypingIndicator()}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </Box>
              {/* Input Area */}
              <ChatInput onSendMessage={handleSendMessage} sending={sending} conversationStarters={messages.length > 0 ? conversationStarters : undefined} />
            </>
          ) : loadingAgent ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" gap={2}>
              <CircularProgress />
              <Typography variant="body1" color="text.secondary">
                Loading chat...
              </Typography>
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography variant="body1" color="text.secondary">
                No agent selected. Please select an agent to start chatting.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {/* AI Disclaimer Footer */}
      <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: "divider", bgcolor: "background.paper" }}>
        <Typography
          variant="caption"
          sx={{
            fontStyle: "italic",
            color: "text.secondary",
            fontSize: "0.75rem",
            lineHeight: 1.2,
          }}
        >
          AI agents may make mistakes, please verify details before approving actions from chat responses.
        </Typography>
      </Box>

      {/* Coach Plan Modal */}
      <CoachPlanModal open={planModalOpen} onClose={() => setPlanModalOpen(false)} plan={currentAgent?.currentPlan || null} coachName={currentAgent?.agentName || "Coach"} />
    </Box>
  );
};
